// Service Google Tasks pour les notes et tâches
// Note: Google Keep n'a pas d'API publique, on utilise Google Tasks à la place
import googleAuthService from './googleAuth';

// Helper pour extraire le texte d'une instruction (peut être string ou objet)
const getInstructionText = (instruction) => {
  if (!instruction) return '';
  if (typeof instruction === 'string') return instruction;
  if (typeof instruction === 'object' && instruction.text) return instruction.text;
  if (typeof instruction === 'object' && instruction.name) return instruction.name;
  return '';
};

const TASKLIST_TITLE = 'ADHD Planner Notes';

class GoogleTasksService {
  constructor() {
    this.taskListId = null;
    this.isInitialized = false;
  }

  // Vérifier si le service est prêt
  isReady() {
    return googleAuthService.checkSignedIn();
  }

  // Initialiser la liste de tâches
  async initTaskList() {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    try {
      // Charger l'API Tasks
      await window.gapi.client.load('tasks', 'v1');

      // Chercher la liste existante
      const response = await window.gapi.client.tasks.tasklists.list();
      const lists = response.result.items || [];
      const existingList = lists.find(l => l.title === TASKLIST_TITLE);

      if (existingList) {
        this.taskListId = existingList.id;
        console.log('Liste Google Tasks trouvée:', this.taskListId);
      } else {
        // Créer la liste
        const createResponse = await window.gapi.client.tasks.tasklists.insert({
          resource: { title: TASKLIST_TITLE },
        });
        this.taskListId = createResponse.result.id;
        console.log('Liste Google Tasks créée:', this.taskListId);
      }

      this.isInitialized = true;
      return this.taskListId;
    } catch (error) {
      // 403 = API non activée dans Google Cloud Console (normal, service optionnel)
      if (error.status === 403 || error.message?.includes('403')) {
        console.info('✅ Google Tasks: API non activée (optionnel)');
      } else {
        console.warn('Google Tasks non disponible:', error.message || error);
      }
      this.isInitialized = false;
      return null;
    }
  }

  // Ajouter une note/tâche
  async addNote(title, notes = '', dueDate = null) {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    if (!this.isInitialized) {
      await this.initTaskList();
    }

    if (!this.taskListId) {
      console.warn('Google Tasks non disponible');
      return null;
    }

    try {
      const task = {
        title: title,
        notes: notes,
      };

      if (dueDate) {
        task.due = new Date(dueDate).toISOString();
      }

      const response = await window.gapi.client.tasks.tasks.insert({
        tasklist: this.taskListId,
        resource: task,
      });

      console.log('Note ajoutée à Google Tasks:', response.result.id);
      return response.result;
    } catch (error) {
      console.error('Erreur ajout note:', error);
      return null;
    }
  }

  // Récupérer toutes les notes
  async getNotes(showCompleted = false) {
    if (!this.isReady() || !this.taskListId) {
      return [];
    }

    try {
      const response = await window.gapi.client.tasks.tasks.list({
        tasklist: this.taskListId,
        showCompleted: showCompleted,
        maxResults: 100,
      });

      return response.result.items || [];
    } catch (error) {
      console.error('Erreur récupération notes:', error);
      return [];
    }
  }

  // Mettre à jour une note
  async updateNote(taskId, updates) {
    if (!this.isReady() || !this.taskListId) {
      return null;
    }

    try {
      const response = await window.gapi.client.tasks.tasks.patch({
        tasklist: this.taskListId,
        task: taskId,
        resource: updates,
      });

      return response.result;
    } catch (error) {
      console.error('Erreur mise à jour note:', error);
      return null;
    }
  }

  // Marquer une note comme complétée
  async completeNote(taskId) {
    return this.updateNote(taskId, { status: 'completed' });
  }

  // Supprimer une note
  async deleteNote(taskId) {
    if (!this.isReady() || !this.taskListId) {
      return false;
    }

    try {
      await window.gapi.client.tasks.tasks.delete({
        tasklist: this.taskListId,
        task: taskId,
      });
      return true;
    } catch (error) {
      console.error('Erreur suppression note:', error);
      return false;
    }
  }

  // Synchroniser les tâches locales vers Google Tasks
  async syncTasksToGoogle(localTasks) {
    if (!this.isReady()) {
      return { synced: 0, errors: [] };
    }

    if (!this.isInitialized) {
      await this.initTaskList();
    }

    const results = { synced: 0, errors: [] };

    for (const task of localTasks) {
      try {
        const title = `[${task.category || 'tâche'}] ${task.task}`;
        const notes = [
          task.desc || '',
          task.time ? `Heure: ${task.time}` : '',
          task.duration ? `Durée: ${task.duration} min` : '',
          task.instructions?.length ? `Étapes: ${task.instructions.map(i => getInstructionText(i)).filter(Boolean).join(', ')}` : '',
        ].filter(Boolean).join('\n');

        await this.addNote(title, notes, task.date);
        results.synced++;
      } catch (error) {
        results.errors.push({ task: task.id, error: error.message });
      }
    }

    return results;
  }

  // Obtenir le statut du service
  getStatus() {
    return {
      isReady: this.isReady(),
      isInitialized: this.isInitialized,
      taskListId: this.taskListId,
    };
  }
}

// Singleton
const googleTasksService = new GoogleTasksService();
export default googleTasksService;
